// Hashing + summarization for report synthesis. The report stores a fingerprint
// of the widget data that produced its analysis (`config.analysis.dataHash`); we
// recompute it from the live widget outputs on view and only re-synthesize when
// it differs. Colocated with the Reports views that consume it.
//
// Each entry passed in is `{ args, title?, data }`: the widget's stored kwargs
// (plus its user-given title, present only when set) and its executeWidget
// output (or null when the widget hasn't loaded successfully).

import objectHash from 'object-hash';

// Project a widget to just the signal the analysis depends on — the entity +
// metrics that buildSynthesisSummary feeds the model, plus the user-given
// title, included ONLY when set so untitled widgets hash exactly as before (an
// unconditional key would mass re-synthesize existing reports). `_rows` is
// excluded: the analysis never reads it, and it's the bulkiest field, so
// hashing it would only slow object-hash and trigger needless re-synthesis on
// row-only changes.
// `follow_ups` / `dashboard_url` fall out naturally (we don't pull them from
// `result`) — they're LLM-regenerated / URL-volatile.
const projectWidget = (args, data, title) => {
    const result = data?.result ?? {};
    return {
        args,
        ...(title ? { title } : {}),
        metrics: result.metrics ?? null,
        entityName: data?._entity?.name ?? null,
    };
};

/**
 * False for an optimistic prune: analysing a stand-in one row short would write
 * an analysis the refetch immediately invalidates. Test the payload, never the
 * query state — `warmWidgetCache` seeds the same cache with real chat data that
 * should synthesize at once, so an `isFetching` gate would stall that path.
 */
export const isAuthoritative = (data) =>
    Boolean(data?.success) && !data?._optimistic;

const hasData = (entry) => isAuthoritative(entry?.data);

/**
 * Order-independent fingerprint of a report's widget data. Each loaded widget is
 * hashed (object-hash is deterministic regardless of object key order), the
 * per-widget hashes are sorted, then combined — so reordering never changes the
 * result, while adding/removing a widget or its data changing does. Entries
 * without successful data are ignored.
 */
export const computeReportDataHash = (entries = []) => {
    const perWidget = entries
        .filter(hasData)
        .map((entry) =>
            objectHash(projectWidget(entry.args, entry.data, entry.title)),
        )
        .sort();
    if (perWidget.length === 0) return null;
    return objectHash(perWidget);
};

/**
 * Compact per-widget summaries to feed the synthesis LLM — entity context plus
 * the formatted metrics it should ground its analysis in. Volatile/bulky fields
 * (time series, dashboard URLs, follow-ups) are left out to bound tokens.
 */
export const buildSynthesisSummary = (entries = []) =>
    entries.filter(hasData).map(({ args, title, data }) => ({
        entity: data._entity?.name ?? null,
        type: data._entity?.type ?? null,
        // Only when set, so the model reads its presence as meaningful.
        ...(title ? { title } : {}),
        relationship: args.entity_relationship ?? 'self',
        geo: data.result?.geo ?? args.geo ?? null,
        data_source: data.result?.data_source ?? args.data_source ?? null,
        metrics: data.result?.metrics ?? [],
    }));
